export interface ConsentResult {
  adUserData: boolean | null
  adPersonalization: boolean | null
}

// External dataLayer shape from Google Consent Mode - keys are snake_case by spec
interface ConsentParams {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  ad_user_data?: string
  // eslint-disable-next-line @typescript-eslint/naming-convention
  ad_personalization?: string
}

const mapConsentValue = (value?: string): boolean | null => {
  switch (value) {
    case 'granted':
      return true
    case 'denied':
      return false
    default:
      return null
  }
}

/**
 * Extracts the latest Google Consent Mode signals from window.dataLayer and maps
 * them to Braze custom attribute values.
 *
 * GTM pushes consent entries via gtag(), which stores them as Arguments objects
 * (not plain arrays) in the format:
 *   Arguments['consent', 'default' | 'update', { ad_user_data: 'granted' | 'denied', ... }]
 *
 * We normalize each entry to an array to handle both Arguments objects and plain
 * arrays. We iterate in reverse so the most recent entry wins, the CMP first
 * pushes a 'default' (denied) entry on cold load, then an 'update' (granted/denied)
 * entry once the shared-consent cookie is read. Returning null means "no signal yet,
 * do not write to Braze".
 */
export const mapConsent = (dataLayer: unknown[]): ConsentResult => {
  for (let i = dataLayer.length - 1; i >= 0; i--) {
    const entry = dataLayer[i]

    if (entry === null || typeof entry !== 'object') {
      continue
    }

    // GTM pushes via gtag() which uses `dataLayer.push(arguments)`, result is
    // an Arguments object, not a plain array. Array.from() normalises both cases.
    const arr = Array.isArray(entry) ? entry : Array.from(entry as ArrayLike<unknown>)

    if (
      arr[0] === 'consent' &&
      (arr[1] === 'default' || arr[1] === 'update') &&
      arr[2] !== null &&
      typeof arr[2] === 'object'
    ) {
      const params = arr[2] as ConsentParams

      return {
        adUserData: mapConsentValue(params.ad_user_data),
        adPersonalization: mapConsentValue(params.ad_personalization),
      }
    }
  }

  return { adUserData: null, adPersonalization: null }
}
